import { CheckCircleOutlined, MinusOutlined } from "@ant-design/icons";
import { TransformationIcon } from "@org-avp/avp-avengers-ui-shared-icons";
import { ColDef, ValueGetterParams, ValueSetterParams } from "ag-grid-community";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MarketingRuleTableContentColumn, MarketingRuleTableRowData } from "./marketing-data-table-utils";

const PositionDataHeader: FC = () => {
  return (<div style={{ textOverflow: "unset" }}><TransformationIcon style={{ fontSize: "18px" }}/></div>);
};

export const useMarketingRuleTableColumnDefinitions = (
  columns: MarketingRuleTableContentColumn[],
  tNS: string,
  tPrefix: string,
) => {
  const { t } = useTranslation(tNS);

  const positionDataIcon = useMemo(() => <div style={{ textOverflow: "unset" }}><TransformationIcon style={{ fontSize: "18px" }}/></div>, []);
  const defaultTransformationIcon = useMemo(() => <div style={{ textOverflow: "unset" }}><CheckCircleOutlined style={{ fontSize: "18px" }}/></div>, []);
  const notDefaultTransformation = useMemo(() => <div style={{ textOverflow: "unset" }}><MinusOutlined style={{ fontSize: "18px" }}/></div>, []);

  return useMemo<ColDef<MarketingRuleTableRowData>[]>(
    () => columns.map((columnName) => {
      switch (columnName) {
        case MarketingRuleTableContentColumn.defaultTransformation:
          return ({
            field: "defaultTransformation",
            headerName: "",
            width: 50,
            maxWidth: 50,
            cellRenderer: ({ data }: ValueGetterParams<MarketingRuleTableRowData>) => (
              data?.defaultTransformation ? defaultTransformationIcon : notDefaultTransformation
            ),
            editable: false,
          });
        case MarketingRuleTableContentColumn.positionData:
          return ({
            field: "positionData",
            headerComponent: PositionDataHeader,
            headerTooltip: t(tPrefix + "header.positionData") as string,
            width: 55,
            maxWidth: 55,
            cellRenderer: ({ data }: ValueGetterParams<MarketingRuleTableRowData>) => (
              data?.positionData ? positionDataIcon : ""
            ),
            editable: false,
          });
        case MarketingRuleTableContentColumn.marketingRule:
          return ({
            field: "marketingRule",
            headerName: t(tPrefix + "header.marketingRule") as string,
            valueSetter: (params: ValueSetterParams<MarketingRuleTableRowData>) => {
              const newValue = params.newValue;
              if (params.data && newValue !== params.data.marketingRule) {
                params.data.marketingRule = newValue;
                return true;
              }
              return false;
            },
            editable: true,
          });
        case MarketingRuleTableContentColumn.conditions:
          return ({
            field: "conditions",
            headerName: t(tPrefix + "header.condition") as string,
            valueSetter: (params: ValueSetterParams<MarketingRuleTableRowData>) => {
              const newValue = params.newValue;
              if (params.data && newValue !== params.data.conditions) {
                params.data.conditions = newValue;
                return true;
              }
              return false;
            },
            editable: true,
          });
        case MarketingRuleTableContentColumn.trigger:
          return ({
            field: "trigger",
            headerName: t(tPrefix + "header.trigger") as string,
            valueSetter: (params: ValueSetterParams<MarketingRuleTableRowData>) => {
              const newValue = params.newValue;
              if (params.data && newValue !== params.data.trigger) {
                params.data.trigger = newValue;
                return true;
              }
              return false;
            },
            editable: true,
          });
        case MarketingRuleTableContentColumn.comment:
          return ({
            field: "comment",
            headerName: t(tPrefix + "header.comment") as string,
            valueSetter: (params: ValueSetterParams<MarketingRuleTableRowData>) => {
              const newValue = params.newValue;
              if (params.data && newValue !== params.data.comment) {
                params.data.comment = newValue;
                return true;
              }
              return false;
            },
            editable: true,
          });
        default:
          // catch-all case: should not happen, but returning undefined would result in a type error
          return { colId: "undefined" };
      }
    })
      // remove catch-all case: should not have happened (see above)
      .filter((it) => it.colId !== "undefined") as ColDef<MarketingRuleTableRowData>[],
    [columns, defaultTransformationIcon, notDefaultTransformation, positionDataIcon, t, tPrefix],
  );
};
